import type { MoneyMapRule } from '@fruitful/types';

export interface BankAccountDisplayDetail {
    id?: string;
    bankAccountId: string;
    icon?: string | null;
    color?: string | null;
    nickname?: string | null;
    accountDetails?: BankAccountDetails;
    logo?: string;
    imgBase64?: string;
    accountNumberMasked?: string;
}

interface BankAccountDetails {
    name?: string | null;
    color?: string | null;
    icon?: string | null;
}

export enum PaycheckRoutingStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    DELETED = 'DELETED',
}

/**
 * How an income split config came to exist. Null on rows created before it
 * was tracked. Authorship of the change lives on the linked money map.
 */
export enum IncomeSplitConfigCreatedVia {
    MEMBER_MONEY_MAP_EDIT = 'MEMBER_MONEY_MAP_EDIT',
    MONEY_MAP_PROPOSAL_ACCEPTED = 'MONEY_MAP_PROPOSAL_ACCEPTED',
    INCOME_SPLIT_EDITOR = 'INCOME_SPLIT_EDITOR',
}

export interface IncomeSplitAccount {
    id?: string;
    paycheckRoutingConfigId?: string;
    accountId: string;
}

export enum IncomeSplitRuleType {
    MINIMUM_BALANCE = 'MINIMUM_BALANCE',
    PERCENTAGE = 'PERCENTAGE',
}

export interface IncomeSplitRuleLegacy {
    id: string;
    amount: number;
    order: number;
    accountId: string;
    paycheckRoutingConfigId?: string;
    type?: IncomeSplitRuleType;
    percentage?: number | null;
    monthlyContributionMaxCents?: number;
    yearlyContributionMaxCents?: number;
    accountType?: PaycheckRoutingRuleAccountType;
    accountMetadata?: PaycheckRoutingRuleAccountMetadata;
    isExternal?: boolean;
    moneyMapRuleId?: string;
}

export interface IncomeSplitConfigLegacy {
    id?: string;
    onlyExecuteOnPaychecks?: boolean;
    accounts: IncomeSplitAccount[];
    rules: IncomeSplitRuleLegacy[];
    isNew?: boolean;
    membershipId: string;
    isActive: boolean;
    status: PaycheckRoutingStatus;
    updatedAt?: Date | null;
    isLegacy?: boolean;
}

export enum PaycheckRoutingRuleAccountType {
    INTERNAL_DEPOSITORY = 'INTERNAL_DEPOSITORY',
    INTERNAL_BROKERAGE = 'INTERNAL_BROKERAGE',
    INTERNAL_INVESTMENT = 'INTERNAL_INVESTMENT',
    EXTERNAL = 'EXTERNAL',
}

export interface PaycheckRoutingRuleAccountMetadata {
    icon?: string | null;
    color?: string | null;
    nickname?: string | null;
    accountNumber?: string | null;
    accountNumberMasked?: string | null;
    accountType?: string | null;
    institutionName?: string | null;
    logo?: string | null;
    imgBase64?: string | null;
}

export class DepositAccount {
    id!: string;
    nickname!: string;
    institutionName?: string;
    bankName?: string;
    logo?: string;
    icon?: string;
    color?: string;
    imgBase64?: string;
    isExternal!: boolean;
    isJoint?: boolean;
    accountNumberMasked?: string;
    availableBalance?: Cents;
}

export class ProposedAccount {
    nickname!: string;
    logo?: string;
    icon?: string;
    color?: string;
    imgBase64?: string;
}

export class AccountSummary {
    id!: string;
    institutionName!: string;
    name!: string;
    accountNumberMasked?: string;
    logo?: string;
    type?: string;
}

export type Cents = number;

export type IncomeSplitReviewContext = 'edit' | 'review';

export interface IncomeSplitRule {
    id?: string;
    originalId?: string;
    incomeSplitConfigId: string;
    incomeSplitRuleSetId?: string;
    priority: number;
    percentage?: number;
    targetBalanceCents?: number;
    accountId: string;
    accountType: PaycheckRoutingRuleAccountType;
    type: IncomeSplitRuleType;
    moneyMapRuleId?: string;
    monthlyContributionMaxCents?: number;
    yearlyContributionMaxCents?: number;
    retirementContributionLimit?: number;
    description?: string;
    isExternal?: boolean;
    accountMetadata?: PaycheckRoutingRuleAccountMetadata;
}

export interface IncomeSplitRuleWithMoneyMapRule extends IncomeSplitRule {
    moneyMapRule?: MoneyMapRule | null;
}

export interface IncomeSplitRuleSet {
    id?: string;
    incomeSplitConfigId: string;
    moneyMapRuleGroupId?: string;
    name?: string;
    priority: number;
    rules: IncomeSplitRule[];
}

export interface IncomeSplitConfig {
    id?: string;
    onlyExecuteOnPaychecks: boolean;
    accounts: IncomeSplitAccount[];
    ruleSets: IncomeSplitRuleSet[];
    status?: PaycheckRoutingStatus;
    isNew?: boolean;
    membershipId?: string;
    updatedAt?: Date | null;
    createdAt?: Date | null;
    /**
     * Money map version this config was generated from. Null for hand-built
     * / legacy configs. "Generated from" is not "still matches."
     */
    moneyMapId?: string | null;
    /**
     * When this config stopped being current. Set together with
     * `status = DELETED` on replacement.
     */
    deletedAt?: Date | string | null;
    /** How this config came to exist. Null before provenance was tracked. */
    createdVia?: IncomeSplitConfigCreatedVia | null;
}

export interface IncomeSplitHistory {
    id: string;
    membershipId: string;
    transactionDate: string;
    transactionAmount: number;
    transactionDescription: string;
    accountId: string;
    emailCopy: string;
}
